import React from 'react';
import PropTypes from 'prop-types';
import { Eye } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import LoadingHint from 'tailwind-components/LoadingHint/LoadingHint';
import PdfBadge from './PdfBadge';
import TablePager from 'tailwind-components/TablePager/TablePager';
import {
  formatCreatedAt,
  formatPeriod,
  formatWeekLabel
} from '../helpers/callReviewsDates';
import { cn } from 'lib/utils';

const headerBase =
  'bg-grey-lightest py-2.5 text-left text-xs font-medium uppercase tracking-wider text-grey-light';

/**
 * the reports table, shared by the agent's own view and the admin drill-down
 * inside an expanded roster row. rows keep the api's order — sorting client side
 * would only reorder the current page, which would be misleading.
 * @param items - AnalysisFileItem rows for the current page
 * @param onOpenFile - called with the row whose pdf should be opened and the agent name
 * @param agentName - agent the rows belong to, forwarded to the preview title
 * @param emptyMessage - copy shown when there are no rows
 * @param compact - tighter spacing, for the table nested inside a roster row
 * @param refreshing - true while a fetch runs with rows already on screen. only the
 *   nested table needs it; the standalone one gets its hint from the card header
 * @param retrying - true while that fetch is being retried after a failure
 * @param error - inline message shown above rows that failed to refresh
 * @param showPager - render the pager under the table; only the nested table needs it,
 *   the standalone one is paged from its card header
 * @param page - current 1-indexed page, required with showPager
 * @param totalPages - total pages reported by the api, required with showPager
 * @param onPageChange - called with the page to move to, required with showPager
 */
export default function FilesTable({
  items,
  onOpenFile,
  agentName,
  emptyMessage = 'No reports yet.',
  compact = false,
  refreshing = false,
  retrying = false,
  error = null,
  showPager = false,
  page = 1,
  totalPages = 0,
  onPageChange
}) {
  const inset = compact ? 'px-3' : 'px-5';
  const cellClassName = cn(inset, compact ? 'py-2.5' : 'py-3.5');
  const headerClassName = cn(headerBase, inset);

  return (
    <div className='flex w-full flex-col'>
      {refreshing && (
        <LoadingHint isRetrying={retrying} className={cn('pb-2', inset)} />
      )}
      {error && (
        <p className={cn('pb-2 text-xs text-semantic-danger', inset)}>
          {error}
        </p>
      )}

      {items.length === 0 && (
        <p
          className={cn(
            'py-8 text-center text-[length:--font-size-body] text-grey',
            inset
          )}
        >
          {emptyMessage}
        </p>
      )}

      {items.length > 0 && (
        <table
          aria-busy={refreshing}
          className={cn(
            'w-full border-collapse transition-opacity',
            refreshing && 'opacity-50'
          )}
        >
          <thead>
            <tr className='border-y border-grey-lighter'>
              <th className={headerClassName}>Date range</th>
              <th className={headerClassName}>Period</th>
              <th className={headerClassName}>Added</th>
              <th className={cn(headerClassName, 'w-px text-right')}>
                <span className='sr-only'>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map(file => (
              <tr
                key={file.file_uuid}
                className='border-b border-grey-lighter transition-colors hover:bg-grey-lightest/60'
              >
                <td className={cellClassName}>
                  <span className='flex items-center gap-3'>
                    <PdfBadge />
                    <span className='font-semibold text-black'>
                      {formatPeriod(file.from_date, file.to_date)}
                    </span>
                  </span>
                </td>
                <td className={cn(cellClassName, 'text-grey')}>
                  {formatWeekLabel(file.from_date)}
                </td>
                <td className={cn(cellClassName, 'text-grey')}>
                  {formatCreatedAt(file.created_at)}
                </td>
                <td className={cn(cellClassName, 'text-right')}>
                  <Button
                    variant='secondary'
                    size='xsmall'
                    text='Open'
                    icon={<Eye />}
                    className='shrink-0'
                    // every row's button reads the same, so name the report it opens
                    aria-label={`Open report for ${formatPeriod(
                      file.from_date,
                      file.to_date
                    )}`}
                    onClick={() => onOpenFile(file, agentName)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showPager && totalPages > 1 && (
        <div className={cn('flex justify-end pb-1 pt-3', inset)}>
          <TablePager
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

FilesTable.propTypes = {
  items: PropTypes.array.isRequired,
  onOpenFile: PropTypes.func.isRequired,
  agentName: PropTypes.string,
  emptyMessage: PropTypes.string,
  compact: PropTypes.bool,
  refreshing: PropTypes.bool,
  retrying: PropTypes.bool,
  error: PropTypes.string,
  showPager: PropTypes.bool,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func
};
